import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Check } from "lucide-react";
import { api, ApiError } from "../api.ts";
import { TOOLS, type Recurrence } from "../../../shared/tools.ts";
import { taskArgsFromDraft, type TaskDraft } from "./task-draft.ts";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card.tsx";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog.tsx";
import { browserTimezone, formatWhen, fromLocalInput, parseReminders, reminderLabel, toLocalInput } from "./format.ts";

export type TaskRecord = { id: string; title: string; details: string; status: "open" | "done"; dueAt: string | null; timezone: string; reminders: number[]; recurrence: Recurrence | null; revision: number; scheduled: { at: string; state: string }[] };
type Draft = TaskDraft;
type ActionResult = { success: boolean; instructionToRealtime: string; error?: string };
const DELIVERY = "Delegate-owned records. Reminders arrive as messages in Delegate.";

/** The person's tasks: a view of real records plus the same typed actions the employee uses, with revisions. */
export function TasksSurface({ avatarId, active, refreshVersion, onEditingChange }: { avatarId: string; active: boolean; refreshVersion: number; onEditingChange: (editing: boolean) => void }) {
  const [tasks, setTasks] = useState<TaskRecord[]>([]);
  const [status, setStatus] = useState<"open" | "done">("open");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [deleting, setDeleting] = useState<TaskRecord | null>(null);
  const pending = useRef<{ name: string; args: unknown; callId: string } | null>(null);
  const submitting = useRef(false);
  const epoch = useRef(0);
  const load = useCallback(async () => {
    const attempt = ++epoch.current;
    setLoading(true);
    try {
      const data = await api<{ tasks: TaskRecord[] }>(`/api/tools/tasks?status=${status}`, { avatarId });
      if (attempt === epoch.current) setTasks(data.tasks);
    } catch {
      if (attempt === epoch.current) setError("Could not load your tasks. Try again.");
    } finally {
      if (attempt === epoch.current) setLoading(false);
    }
  }, [avatarId, status]);
  useEffect(() => { if (active) void load(); return () => { epoch.current++; }; }, [active, refreshVersion, load]);
  useEffect(() => { onEditingChange(!!draft); }, [draft, onEditingChange]);
  useEffect(() => () => onEditingChange(false), [onEditingChange]);

  const run = async (name: string, args: unknown, done: string) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true); setError(null); setNotice("");
    const command = pending.current ?? { name, args, callId: `manual_${crypto.randomUUID()}` };
    pending.current = command;
    try {
      const { result } = await api<{ result: ActionResult }>("/api/tools/actions", { avatarId, body: command });
      pending.current = null; setUncertain(false);
      if (!result.success) { setError(result.instructionToRealtime); await load(); return; }
      setDraft(null); setDeleting(null); setNotice(done); await load();
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        pending.current = null; setUncertain(false); setDeleting(null);
        setError(e.status === 409 ? "This task changed while you were editing. Your draft is kept; compare it with the saved task below, then cancel and reopen it to use the latest version." : e.status === 401 ? "Your sign-in expired. Sign in again to continue." : "The task could not be saved. Check its details and refresh.");
        await load(); return;
      }
      setUncertain(true);
      setError("The response was lost. Check the result before making more changes; this uses the same request and cannot save a duplicate.");
    } finally { submitting.current = false; setBusy(false); }
  };
  const save = (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    const args = taskArgsFromDraft(draft, draft.id ? tasks.find((t) => t.id === draft.id) : undefined);
    const schema = draft.id ? TOOLS.tasks.actions.tool_tasks_update.schema : TOOLS.tasks.actions.tool_tasks_create.schema;
    const parsed = schema.safeParse(draft.id ? { ...args, id: draft.id, revision: draft.revision } : args);
    if (!parsed.success) { setError("Give the task a title (up to 200 characters), a valid date, and reminders as minutes before, e.g. 30, 1440."); return; }
    void run(draft.id ? "tool_tasks_update" : "tool_tasks_create", parsed.data, "Task saved.");
  };
  const edit = (t?: TaskRecord) => {
    setError(null); setNotice("");
    setDraft(t
      ? { id: t.id, revision: t.revision, title: t.title, details: t.details, due: toLocalInput(t.dueAt, t.timezone), timezone: t.timezone, reminders: t.reminders.join(", "), freq: t.recurrence?.freq ?? "", interval: String(t.recurrence?.interval ?? 1) }
      : { title: "", details: "", due: "", timezone: browserTimezone(), reminders: "", freq: "", interval: "1" });
  };
  const locked = !!draft || busy || uncertain;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{DELIVERY}</p>
        <div className="flex gap-2">
          <Button variant={status === "open" ? "secondary" : "ghost"} size="sm" onClick={() => setStatus("open")} aria-pressed={status === "open"}>Open</Button>
          <Button variant={status === "done" ? "secondary" : "ghost"} size="sm" onClick={() => setStatus("done")} aria-pressed={status === "done"}>Done</Button>
          <Button variant="outline" disabled={locked} onClick={() => edit()}><Plus /> New task</Button>
        </div>
      </div>
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      {notice && <p role="status" className="text-sm text-success-foreground">{notice}</p>}
      {uncertain && <Button disabled={busy} onClick={() => { if (pending.current) void run(pending.current.name, pending.current.args, "Saved."); }}>{busy ? "Checking..." : "Check result"}</Button>}
      {draft && (
        <form onSubmit={save} className="space-y-4 rounded-xl border border-border p-4" aria-label="Edit task">
          <p className="text-sm font-medium">You're editing. Your employee will wait before changing your tasks.</p>
          <div className="space-y-2"><Label htmlFor="task-title">Title</Label><Input id="task-title" required maxLength={200} disabled={busy || uncertain} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
          <div className="space-y-2"><Label htmlFor="task-details">Details</Label><Textarea id="task-details" rows={3} maxLength={4000} disabled={busy || uncertain} value={draft.details} onChange={(e) => setDraft({ ...draft, details: e.target.value })} /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label htmlFor="task-due">Due ({draft.timezone})</Label><Input id="task-due" type="datetime-local" disabled={busy || uncertain} value={draft.due} onChange={(e) => setDraft({ ...draft, due: e.target.value })} /></div>
            <div className="space-y-2"><Label htmlFor="task-reminders">Reminders (minutes before, comma-separated)</Label><Input id="task-reminders" placeholder="30, 1440" disabled={busy || uncertain || !draft.due} value={draft.reminders} onChange={(e) => setDraft({ ...draft, reminders: e.target.value })} /></div>
            <div className="space-y-2"><Label htmlFor="task-freq">Repeats</Label>
              <select id="task-freq" className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" disabled={busy || uncertain || !draft.due} value={draft.freq} onChange={(e) => setDraft({ ...draft, freq: e.target.value as Draft["freq"] })}>
                <option value="">Never</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option>
              </select>
            </div>
            {draft.freq && <div className="space-y-2"><Label htmlFor="task-interval">Every</Label><Input id="task-interval" type="number" min={1} max={52} disabled={busy || uncertain} value={draft.interval} onChange={(e) => setDraft({ ...draft, interval: e.target.value })} /></div>}
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || uncertain}>{busy ? "Saving..." : "Save task"}</Button>
            <Button variant="ghost" type="button" disabled={busy || uncertain} onClick={() => { setDraft(null); setError(null); }}>Cancel editing</Button>
          </div>
        </form>
      )}
      {loading && <p role="status" className="text-sm text-muted-foreground">Loading tasks...</p>}
      {!loading && !tasks.length && <p className="text-sm text-muted-foreground">{status === "open" ? "No open tasks. Ask your employee to register one, or add it yourself." : "Nothing completed yet."}</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        {tasks.map((t) => (
          <Card key={t.id}>
            <CardHeader><CardTitle className="break-words">{t.title}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {t.details && <p className="whitespace-pre-wrap break-words text-sm">{t.details}</p>}
              <p className="text-sm">{formatWhen(t.dueAt, t.timezone)}</p>
              <div className="flex flex-wrap gap-2">
                {t.recurrence && <Badge variant="outline">Repeats {t.recurrence.freq}{t.recurrence.interval && t.recurrence.interval > 1 ? ` x${t.recurrence.interval}` : ""}</Badge>}
                {t.reminders.map((m) => <Badge key={m} variant="secondary">Reminder {reminderLabel(m)}</Badge>)}
                {t.scheduled.map((s) => <Badge key={s.at} variant={s.state === "delivered" ? "default" : s.state === "missed" ? "destructive" : "outline"}>{s.state} | {formatWhen(s.at, t.timezone)}</Badge>)}
              </div>
              <p className="text-xs text-muted-foreground">Saved | version {t.revision}</p>
              <div className="flex gap-2">
                {t.status === "open" && <Button variant="outline" size="sm" disabled={locked} onClick={() => void run("tool_tasks_complete", { id: t.id, revision: t.revision }, "Task completed.")}><Check /> Done</Button>}
                <Button variant="outline" size="sm" disabled={locked} onClick={() => edit(t)}><Pencil /> Edit</Button>
                <Button variant="ghost" size="sm" disabled={locked} onClick={() => setDeleting(t)}><Trash2 /> Delete</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Button variant="ghost" onClick={() => void load()} disabled={loading}>Refresh tasks</Button>
      <AlertDialog open={!!deleting} onOpenChange={(open) => { if (!open && !busy && !uncertain) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this task?</AlertDialogTitle><AlertDialogDescription>"{deleting?.title}" and its pending reminders will be removed.</AlertDialogDescription></AlertDialogHeader>
          {uncertain && <Alert variant="destructive"><AlertDescription>The result is unknown. Check it before continuing.</AlertDescription></Alert>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy || uncertain}>Keep task</AlertDialogCancel>
            <AlertDialogAction variant="destructive" disabled={busy} onClick={(e) => { e.preventDefault(); if (deleting) void run("tool_tasks_delete", { id: deleting.id, revision: deleting.revision }, "Task deleted."); }}>{busy ? "Deleting..." : uncertain ? "Check result" : "Delete task"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
